import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  VpnKey as VpnKeyIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthContext";
import type { AuthClient } from "../authClient";
import { AuthPage } from "./AuthPage";

export interface AuthResetPasswordProps {
  appTitle: string;
  logo: string;
  authClient: AuthClient;
}

export function ResetPassword({
  appTitle,
  logo,
  authClient,
}: AuthResetPasswordProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const token = searchParams.get("resttoken");

  useEffect(() => {
    if (!token) {
      setError("InvalidResetToken");
    }
  }, [token]);

  const validatePasswordRules = (pwd: string): string | null => {
    if (pwd.length < 6) return "PasswordMinLength";
    if (!/(?=.*?[0-9])/.test(pwd)) return "PasswordMustContainNumber";
    if (!/(?=.*[a-z])/.test(pwd)) return "PasswordMustContainLowercase";
    if (!/(?=.*[A-Z])/.test(pwd)) return "PasswordMustContainUppercase";
    if (!/(?=.*[!@#$%^&*])/.test(pwd)) return "PasswordMustContainSpecialChar";
    if (pwd.length > 20) return "PasswordMaxLength";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email) {
      setError("EnterEmailAddress");
      return;
    }
    if (!password) {
      setError("EnterNewPassword");
      return;
    }
    if (password !== confirmPassword) {
      setError("PasswordMatch");
      return;
    }
    const passwordError = validatePasswordRules(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (!token) {
      setError("InvalidResetToken");
      return;
    }

    setLoading(true);
    try {
      await authClient.resetPassword(email, password, token);
      setSuccess(true);
      setTimeout(() => {
        navigate(isAuthenticated ? "/profile" : "/login");
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "ResetPasswordFailed");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthPage appTitle={appTitle} logo={logo}>
        <Alert severity="success" sx={{ width: "100%", mb: 3 }}>
          {t("PasswordResetSuccess")}
        </Alert>
      </AuthPage>
    );
  }

  return (
    <AuthPage appTitle={appTitle} logo={logo}>
      <Typography
        component="h2"
        variant="body1"
        sx={{ mb: 1, color: "text.secondary" }}
      >
        {t("ResetPassword")}
      </Typography>
      <Typography
        variant="body2"
        sx={{ mb: 4, color: "text.secondary", textAlign: "center" }}
      >
        {t("EnterEmailAndPassword")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ width: "100%", mb: 3, borderRadius: 2 }}>
          {t(error)}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label={t("Email")}
          name="email"
          type="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <EmailIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label={t("NewPassword")}
          type={showPassword ? "text" : "password"}
          id="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={{ mb: 2 }}
          helperText={t("ResetPasswordRequirements")}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="confirmPassword"
          label={t("ConfirmPassword")}
          type={showConfirmPassword ? "text" : "password"}
          id="confirmPassword"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          sx={{ mb: 3 }}
          error={confirmPassword !== "" && password !== confirmPassword}
          helperText={
            confirmPassword !== "" && password !== confirmPassword
              ? t("PasswordMatch")
              : ""
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading || !token}
          startIcon={loading ? <CircularProgress size={20} /> : <VpnKeyIcon />}
          sx={{ py: 1.5, fontSize: "1rem", fontWeight: 600 }}
        >
          {loading ? t("ResettingPassword") : t("ResetPassword")}
        </Button>
      </Box>

      <Box sx={{ mt: 3, textAlign: "center" }}>
        <Button
          variant="text"
          onClick={() => navigate("/login")}
          sx={{ textTransform: "none" }}
        >
          {t("BackToLogin")}
        </Button>
      </Box>
    </AuthPage>
  );
}
